import { TrendingUp, TrendingDown } from "lucide-react";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { DASHBOARD_SPARKLINE_COLORS } from "@/lib/dashboardColors";
import { formatCurrency } from "@/utils/format";

const formatPercent = (value) =>
  new Intl.NumberFormat("de-DE", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format((value || 0) / 100);

export default function MetricBreakdownCard({
  title,
  items = [],
  testId,
  changePercent,
  comparisonLabel,
  comparisonRangeLabel,
  comparisonValue,
  sparklineData = [],
  sparklineColor = DASHBOARD_SPARKLINE_COLORS.revenue,
  invertTrend = false,
  showComparison = true,
}) {
  const hasChange = typeof changePercent === "number";
  const trend = hasChange ? Math.sign(changePercent) : 0;
  const trendColor = invertTrend
    ? trend > 0
      ? "text-red-600"
      : trend < 0
        ? "text-green-600"
        : "text-gray-600"
    : trend > 0
      ? "text-green-600"
      : trend < 0
        ? "text-red-600"
        : "text-gray-600";
  const TrendIcon = trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : null;
  const hasComparison = typeof comparisonValue === "number";
  const comparisonText = hasComparison
    ? `${comparisonLabel || "Vorperiode"}${comparisonRangeLabel ? ` (${comparisonRangeLabel})` : ""}: ${formatCurrency(comparisonValue)}`
    : null;

  return (
    <Card data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between" data-testid={`${testId}-header`}>
          <div className="text-sm font-semibold text-slate-700" data-testid={`${testId}-title`}>
            {title}
          </div>
          {TrendIcon && (
            <TrendIcon className={`h-4 w-4 ${trendColor}`} data-testid={`${testId}-trend-icon`} />
          )}
        </div>
        <div className="mt-4 space-y-3">
          {items.map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-between text-sm"
              data-testid={item.testId}
            >
              <span className="text-slate-500">{item.label}</span>
              <span className="font-semibold text-slate-900">{formatCurrency(item.value)}</span>
            </div>
          ))}
        </div>
        {showComparison && (
          <>
            <div className={`mt-3 text-xs ${trendColor}`} data-testid={`${testId}-change`}>
              {hasChange ? `${formatPercent(changePercent)} ggü. Vorperiode` : "—"}
            </div>
            {comparisonText && (
              <div className="text-[11px] text-gray-500" data-testid={`${testId}-comparison`}>
                {comparisonText}
              </div>
            )}
          </>
        )}
        {sparklineData.length > 1 && (
          <div className="mt-3 h-10" data-testid={`${testId}-sparkline`}>
            <ResponsiveContainer width="100%" height="100%" minHeight={40} minWidth={80}>
              <LineChart data={sparklineData}>
                <Line type="monotone" dataKey="value" stroke={sparklineColor} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}